import { Auth0DecodedHash, WebAuth } from 'auth0-js';

import { UiAuth0Config } from './adl-gen/totalinteraction/futurefit/uiconfig';
import { Service } from './service/service';
import { TokenManager } from './service/token-manager';
import { Router } from './routing/router';

// Handle auth0 integration.

/// When Auth happpens via handledAuthRedir we have to go and clean it up using the router ('auth handled; redirect to home')
export type AuthEventSource = 'checkedSession' | 'handledAuthRedir';

export type OnAuthEventCallback = (isLoggedIn: boolean, source: AuthEventSource, context?: string) => void;

export class Auth implements TokenManager {
  auth0: WebAuth;

  tokenRenewalTimeout: number | undefined;

  storage: Map<string, string>;

  onAuthEvent: OnAuthEventCallback = () => { };

  service?: Service;

  router?: Router;

  constructor(private readonly origin: string, config: UiAuth0Config) {
    this.auth0 = new WebAuth({
      domain: config.domain,
      clientID: config.clientId,
      redirectUri: origin + '/auth0-callback',
      responseType: 'token id_token',
      scope: 'openid profile email'
    });
    this.storage = new Map<string, string>();
  }

  async init(): Promise<void> {
    let isLoggedIn = await this.updateSession('auth:init');
    // If not logged in after initial checkSession, check if this is the callback from Auth0 first
    if (!isLoggedIn) {
      isLoggedIn = await this.handleCallback('auth:init');
      this.onAuthEvent(isLoggedIn, 'handledAuthRedir', 'auth:init');
    } else
      this.onAuthEvent(isLoggedIn, 'checkedSession', 'auth:init');
  }

  public setOnAuthEvent(callback: OnAuthEventCallback) {
    this.onAuthEvent = callback;
  }

  public setService(service: Service) {
    this.service = service;
  }

  public setRouter(router: Router) {
    this.router = router;
  }

  /// Redirect to Auth0 login page
  public login(context?: string) {
    // tslint:disable-next-line: no-console
    console.debug(context, 'login');
    this.auth0.authorize({ responseType: 'token id_token' });
  }

  async handleCallback(context?: string): Promise<boolean> {
    // Check if we are working with the callback
    if (!this.router) {
      // tslint:disable-next-line: no-console
      console.error(context, 'auth.handleCallback: Auth not initialized (no router)')
    }

    else if (this.router.matches('/auth0-callback.*')) {
      // tslint:disable-next-line: no-console
      console.info('handleCallback: handle auth');
      const isLoggedIn = await this.handleAuthentication(context);
      return isLoggedIn;
    }
    return false;

  }

  /// Handle aftermath of Auth0 login.
  /// Saves *Auth0* token, which by default contains no app-specific data (role, clientID).
  ///  - idToken is fetched using getToken
  ///  - accessToken is not used
  async handleAuthentication(context?: string): Promise<boolean> {
    // tslint:disable-next-line: no-console
    console.info('Auth handleAuthentication 0');
    try {
      const isLoggedIn = await this.parseHash(context);
      return isLoggedIn;
    } catch (err) {
      // tslint:disable-next-line: no-console
      console.error('parseHash error', err);
    }
    return false;
  }

  async parseHash(context?: string): Promise<boolean> {
    // Set option to enable IdP-initiated SSO login;
    // @see https://auth0.com/docs/authenticate/protocols/saml/saml-sso-integrations/identity-provider-initiated-single-sign-on
    return new Promise((resolve, reject) =>
      this.auth0.parseHash({ hash: window.location.hash, __enableIdPInitiatedLogin: true },
        (err, authResult) => {
          // tslint:disable-next-line: no-console
          console.info('Auth handleAuthentication 1');
          if (err) {
            // tslint:disable-next-line: no-console
            console.debug(context, `handleAuthentication error: `, err.error);
            reject(false);
          }
          // tslint:disable-next-line: no-console
          console.debug(context, `Auth handleAuthentication 2`);
          if (authResult && authResult.accessToken && authResult.idToken) {
            this.setSession(authResult, context);
            resolve(true);
          } else {
            // Handle a null result
            // tslint:disable-next-line: no-console
            console.debug(context, 'handleAuthentication: null or invalid session extracted');
            resolve(false);
          }
        }
      ));
  }

  /// Save idToken and accessToken.
  setSession(authResult: Auth0DecodedHash, context?: string) {
    // Set the time that the Access Token will expire at

    // tslint:disable-next-line: no-non-null-assertion
    const expiresIn = authResult.expiresIn!;
    // tslint:disable-next-line: no-non-null-assertion
    const accessToken = authResult.accessToken!;
    // tslint:disable-next-line: no-non-null-assertion
    const idToken = authResult.idToken!;

    // tslint:disable-next-line: no-console
    console.debug(context, 'setSession');
    // console.debug('Auth - setSession access_token:', accessToken, ' id_token:', idToken);

    const expiresAt = JSON.stringify(expiresIn * 1000 + new Date().getTime());
    this.setStoredItem('access_token', accessToken);
    this.setStoredItem('id_token', idToken);
    this.setStoredItem('expires_at', expiresAt);
    this.scheduleRenewal(context);
  }

  logout() {
    this.removeStoredItems();
    this.auth0.logout({
      returnTo: this.origin + '/'
    });
    window.clearTimeout(this.tokenRenewalTimeout);
  }

  private removeStoredItems() {
    // Clear Access Token and ID Token
    this.removeStoredItem('access_token');
    this.removeStoredItem('id_token');
    this.removeStoredItem('expires_at');
  }

  /// Just checks expiry, no auth flow done
  isAuthenticated() {
    // Check whether the current time is past the
    // Access Token's expiry time
    const eat = this.getStoredItem('expires_at');
    if (eat) {
      const expiresAt = JSON.parse(eat);
      return new Date().getTime() < expiresAt;
    }
    return false;
  }

  /** Silently check if logged in on auth0 already */
  // Uses different endpoint at Auth0; runs in iframe so no redirection is required
  async checkSession(context?: string): Promise<Auth0DecodedHash | null> {
    return new Promise(resolve => {
      this.auth0.checkSession({}, (err, result) => {
        if (err) {
          // tslint:disable-next-line: no-console
          console.debug(context, 'checkSession error ', err.error);
          resolve(null);
        } else {
          // tslint:disable-next-line: no-console
          console.debug(context, 'checkSession result ', result);
          resolve(result);
        }
      });
    });
  }

  /// Sets *Auth0* token, not server token
  async updateSession(context?: string): Promise<boolean> {
    const sessionOpt: Auth0DecodedHash | null = await this.checkSession(context + ':updateSession');
    // tslint:disable-next-line: no-console
    console.debug(context + ':updateSession - checkSession result ', (sessionOpt === null) ? 'null' : 'active session');
    if (sessionOpt === null) {
      this.removeStoredItems();
      return false;
    } else {
      this.setSession(sessionOpt, context + ':updateSession');
      return true;
    }
  }

  /// Sets *Auth0* token, not server token, so new token will fail server auth unless it has
  /// server claims in it (roles, clientID).
  /// This is then used in a call to /user/login, which fetches the (internal) server JWT for
  /// use with the rest of the API. See IdentityService.init()
  /// call to onAuthEvent calls onRouteChange if valid = false, but if renewal works this doesn't happen.
  ///
  renewToken() {
    /// Set Auth0 token...
    this.updateSession('(async)renewToken')
      .then(valid => {
        if (!valid) {
          this.onAuthEvent(false, 'checkedSession', '(async)renewToken');
        }
        /// Set up for serial call to /user/login
        return this.service;
      })
      .then(service => {
        if (service) {
          // tslint:disable-next-line: no-floating-promises
          service.userRegisterLogin().then(resp => {
            if (resp.kind === 'user') {
              /// Set server token - reach in and overwrite just as on IdentityService.init()
              this.setToken(resp.value.accessToken);
            } else {
              this.onAuthEvent(false, 'checkedSession', '(async)renewToken');
            }
          });
        } else {
          this.onAuthEvent(false, 'checkedSession', '(async)renewToken');
        }
      })
      .catch(_err => {
        // tslint:disable-next-line: no-console
        console.error('Error updating renewToken');
        this.onAuthEvent(false, 'checkedSession', '(async)renewToken');
      });
  }

  scheduleRenewal(context?: string) {
    const expiresAtItem = this.getStoredItem('expires_at');
    if (expiresAtItem !== null) {
      const expiresAt: number = JSON.parse(expiresAtItem);
      const delay = expiresAt - Date.now();
      if (delay > 0) {
        // tslint:disable-next-line: no-console
        console.debug(context, 'scheduling token renewal in ', delay);

        this.tokenRenewalTimeout = window.setTimeout(() => {
          this.renewToken();
        }, delay);
      }
    }
  }

  hasToken(): boolean {
    const token = this.getStoredItem('id_token');
    return token !== null;
  }

  getToken(): string | null {
    return this.getStoredItem('id_token');
  }

  setToken(token: string): void {
    this.setStoredItem('id_token', token);
  }

  getStoredItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  removeStoredItem(key: string): void {
    this.storage.delete(key);
  }

  setStoredItem(key: string, value: string): void {
    this.storage.set(key, value);
  }
}



// WEBPACK FOOTER //
// ./src/auth.ts